import React, { ReactNode, useMemo } from 'react';
import { formatAtStation } from '../../../utils/stationTime';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { OrderForAssetAvailability } from '../../../api/types';
import { makeRentalBookingDetailsUrl } from '../../../routes';
import { statusColors } from './availabilityStatus';
import {
    DaySpan,
    formatDaySpan,
    freeWindowsForDay,
    isFreeAllDay,
    orderSpanWithinDay,
    spanToTrackStyle,
} from './dayOrders';

// One object's day, on a 24-hour timeline: every booking as a status-coloured
// bar, and underneath it the stretches that are still free.
//
// Used by both day surfaces — the single-asset dialog (click a cell) and the
// all-objects overlay (click a column header) — so the two can never drift
// apart. It renders a `<tbody>` rather than a whole table so the overlay can
// stack thirty objects under one shared hour ruler and have every bar line up.

// Ruler marks every three hours: 24 labels do not fit at this width, and three
// hours is fine enough to read a bar's position off the scale.
const RULER_STEP_HOURS = 3;
const RULER_HOURS = Array.from(
    { length: 24 / RULER_STEP_HOURS },
    (_, index) => index * RULER_STEP_HOURS,
);

/** Width of the details column. Fixed so every section's bars align. */
const LABEL_COLUMN = 'w-44';

interface DayTimelineTrackProps {
    /** Base of the track — the part *not* covered by the children. */
    trackClassName?: string;
    children?: ReactNode;
}

/** The 24-hour lane a bar is drawn on, with its three-hourly gridlines. */
const DayTimelineTrack: React.FC<DayTimelineTrackProps> = (
    { trackClassName = 'bg-foreground/5', children },
) => (
    <div className={`relative h-5 rounded-sm overflow-hidden ${trackClassName}`}>
        {RULER_HOURS.filter((hour) => hour > 0).map((hour) => (
            <div
                key={hour}
                className={'absolute inset-y-0 w-px bg-foreground/10'}
                style={{ left: `${(hour / 24) * 100}%` }}
            />
        ))}
        {children}
    </div>
);

interface DayTimelineHeadProps {
    /** Pins the ruler while the overlay's object list scrolls under it. */
    sticky?: boolean;
}

/** The shared table head: details column label plus the hour scale. */
export const DayTimelineHead: React.FC<DayTimelineHeadProps> = ({ sticky = false }) => {
    const { t } = useTranslation();
    // Sticky needs its own background, or the rows scroll through it, and its
    // own top padding, or they touch the label while sliding under.
    const cellClass = sticky
        ? 'sticky top-0 z-10 bg-text-contrast pt-2'
        : '';

    return (
        <thead>
            <tr className={'text-foreground/60 font-medium'}>
                <th className={
                    `${LABEL_COLUMN} text-left font-medium pb-2 pr-3 ${cellClass}`
                }
                >
                    {t('dashboard.assets_availability.day_view.booking')}
                </th>
                <th className={`font-medium pb-2 ${cellClass}`}>
                    <div className={'flex text-[10px] text-foreground/50'}>
                        {RULER_HOURS.map((hour) => (
                            <div key={hour} className={'flex-1 text-left'}>
                                {String(hour).padStart(2, '0')}
                            </div>
                        ))}
                    </div>
                </th>
            </tr>
        </thead>
    );
};

interface AssetDayOrdersProps {
    /** The calendar day being shown. */
    day: Date;
    /** That day's orders for one object, already filtered like the table. */
    orders: OrderForAssetAvailability[];
    /** Section header — the overlay stacks many objects in a single table. */
    heading?: ReactNode;
}

const AssetDayOrders: React.FC<AssetDayOrdersProps> = ({ day, orders, heading }) => {
    const { t } = useTranslation();

    const rows = useMemo(
        () => orders
            .map((order) => ({ order, span: orderSpanWithinDay(order, day) }))
            .sort((a, b) => a.span.startMinute - b.span.startMinute ||
                a.order.bookingNumber.localeCompare(b.order.bookingNumber)),
        [orders, day],
    );

    const freeWindows = useMemo(() => freeWindowsForDay(orders, day), [orders, day]);

    const freeSummary = (windows: DaySpan[]): string => {
        if (windows.length === 0) {
            return t('dashboard.assets_availability.day_view.no_free_time');
        }
        if (isFreeAllDay(windows)) {
            return t('dashboard.assets_availability.day_view.free_all_day');
        }
        return windows.map(formatDaySpan).join(' · ');
    };

    return (
        <tbody>
            {heading && (
                <tr>
                    <td colSpan={2} className={'pt-5 pb-1'}>
                        {heading}
                    </td>
                </tr>
            )}

            {rows.map(({ order, span }) => (
                <tr
                    key={order.orderId}
                    className={'border-t border-foreground/5 align-top'}
                >
                    <td className={`${LABEL_COLUMN} py-1.5 pr-3`}>
                        <Link
                            to={makeRentalBookingDetailsUrl(order.orderId)}
                            className={
                                'font-medium text-foreground ' +
                                'hover:text-primary hover:underline transition-colors'
                            }
                        >
                            {order.bookingNumber}
                        </Link>
                        <div className={
                            'mt-0.5 flex items-center gap-1.5 text-xs text-foreground/60'
                        }
                        >
                            <span className={
                                'inline-block h-2 w-2 rounded-full shrink-0 ' +
                                statusColors[order.status]
                            }
                            />
                            <span>
                                {t(`dashboard.assets_availability.status.${order.status}`)}
                            </span>
                        </div>
                        <div className={'text-xs text-foreground/60'}>
                            {formatDaySpan(span)}
                        </div>
                    </td>
                    <td className={'py-1.5'}>
                        <DayTimelineTrack>
                            {/* A booking shorter than a few minutes still has to be
                                visible, hence the minimum width. */}
                            <div
                                className={`absolute inset-y-0 rounded-sm ${statusColors[order.status]}`}
                                style={{ ...spanToTrackStyle(span), minWidth: '3px' }}
                                title={
                                    `${formatAtStation(order.startDate, 'dd.MM.yyyy HH:mm')} – ` +
                                    `${formatAtStation(order.endDate, 'dd.MM.yyyy HH:mm')}`
                                }
                            />
                        </DayTimelineTrack>
                    </td>
                </tr>
            ))}

            {/* The free lane is the point of the view: colour means taken, so
                free is left uncoloured — white windows on a grey day. */}
            <tr className={'border-t border-foreground/5 align-top'}>
                <td className={`${LABEL_COLUMN} py-1.5 pr-3 text-xs text-foreground/60`}>
                    {t('dashboard.assets_availability.day_view.free')}
                </td>
                <td className={'py-1.5'}>
                    <DayTimelineTrack trackClassName={'bg-foreground/10'}>
                        {freeWindows.map((freeWindow) => (
                            <div
                                key={freeWindow.startMinute}
                                className={
                                    'absolute inset-y-0 rounded-sm bg-text-contrast ' +
                                    'ring-1 ring-inset ring-foreground/15'
                                }
                                style={spanToTrackStyle(freeWindow)}
                            />
                        ))}
                    </DayTimelineTrack>
                    <div className={'mt-1 text-xs text-foreground/60'}>
                        {freeSummary(freeWindows)}
                    </div>
                </td>
            </tr>
        </tbody>
    );
};

export default AssetDayOrders;
